import React from 'react';
import { classNames } from '../../../../utils';
import { ModelGradientProps } from '../ModelGradient.types';

// Dear reader,
// I didn't mean to write this code like this.
// I'm not proud of it. I'm sorry.
// I started with using vanilla extract and our styling API, but it was hard to translate ChatGPT's / Claude's / Cursor's suggestions into a working solution.
// So I gave up and wrote the code like this.

export function ImageModelGradient({
    colors,
    rotate,
    animate,
    scale,
    borderRadius,
}: Omit<ModelGradientProps, 'gradient'>) {
    const initialHeights = ['25%', '60%', '80%', '65%', '45%', '30%', '70%', '40%', '55%'];
    let scaleValue = 1;
    if (scale && scale !== 'none') {
        scaleValue = {
            s: 1.6,
            m: 2,
            l: 2.4,
            xl: 2.8,
        }[scale];
    }
    const [mainColor, secondaryColor, lightColor] = colors;

    const gradientWrapperStyle = {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius,
        ['--main-color' as string]: mainColor,
        ['--secondary-color' as string]: secondaryColor,
        ['--light-color' as string]: lightColor ?? '#cdcdcd', // TODO: remove this fallback?,
        background: `linear-gradient(90deg, var(--main-color) 0%, var(--secondary-color) 50%, var(--light-color) 100%)`,
    };

    const shouldStretchGradient = rotate === 90 || rotate === 270;
    const gradientStyle = {
        ['--rotate' as string]: rotate,
        ['--scale' as string]: scaleValue,
        width: '100%',
        height: shouldStretchGradient ? '180%' : '100%',
        display: 'flex',
        gap: 0,
        background: 'none',
        padding: 0,
        overflow: 'hidden',
        transformOrigin: 'center',
        scale: `calc(var(--scale, 1))`,
        rotate: `calc(var(--rotate) * 1deg)`,
    };

    const getGradientColumnStyle = (index: number, initialHeight: string) => ({
        ['--index' as string]: index + 1,
        ['--initial-height' as string]: initialHeight,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        height: '100%',
    });

    return (
        <div style={gradientWrapperStyle}>
            <div
                className={classNames(
                    animate === 'hover' && 'image-model-gradient-animate-hover',
                    animate === 'always' && 'image-model-gradient-animate-always'
                )}
                style={gradientStyle}
            >
                {[...Array(9)].map((_, index) => {
                    const initialHeight = initialHeights[index];
                    const fallbackGradient = {
                        top: `linear-gradient(to bottom, var(--main-color) 0%, var(--main-color) 5%, var(--secondary-color) 100%)`,
                        bottom: `linear-gradient(to bottom, var(--main-color) 0%, var(--secondary-color) 100%)`,
                    };
                    const gradients =
                        {
                            1: {
                                top: `linear-gradient(to bottom, var(--main-color) 0%, var(--main-color) 5%, var(--secondary-color) 100%)`,
                                bottom: `linear-gradient(to bottom, var(--main-color) 0%, var(--secondary-color) 100%)`,
                            },
                            2: {
                                top: `linear-gradient(to bottom, var(--main-color) 0%, var(--main-color) 10%, var(--secondary-color) 50%, var(--secondary-color) 100%)`,
                                bottom: `linear-gradient(to bottom, var(--main-color) 0%, var(--secondary-color) 100%)`,
                            },
                            3: {
                                top: `linear-gradient(to bottom, var(--main-color) 0%, var(--secondary-color) 50%, var(--light-color) 100%)`,
                                bottom: `linear-gradient(to bottom, var(--main-color) 0%, var(--secondary-color) 100%)`,
                            },
                            4: {
                                top: `linear-gradient(to bottom, var(--light-color) 0%, var(--light-color) 20%, var(--secondary-color) 100%)`,
                                bottom: `linear-gradient(to bottom, var(--main-color) 0%, var(--secondary-color) 100%)`,
                            },
                            5: {
                                top: `linear-gradient(to bottom, var(--main-color) 0%, var(--main-color) 5%, var(--secondary-color) 100%)`,
                                bottom: `linear-gradient(to bottom, var(--main-color) 0%, var(--secondary-color) 100%)`,
                            },
                            6: {
                                top: `linear-gradient(to bottom, var(--main-color) 0%, var(--main-color) 5%, var(--secondary-color) 100%)`,
                                bottom: `linear-gradient(to bottom, var(--main-color) 0%, var(--secondary-color) 100%)`,
                            },
                            7: {
                                top: `linear-gradient(to bottom, var(--light-color) 0%, var(--light-color) 20%, var(--main-color) 100%)`,
                                bottom: `linear-gradient(to bottom, var(--secondary-color) 0%, var(--light-color) 100%)`,
                            },
                            8: {
                                top: `linear-gradient(to bottom, var(--secondary-color) 0%, var(--secondary-color) 30%, var(--light-color) 100%)`,
                                bottom: `linear-gradient(to bottom, var(--main-color) 0%, var(--secondary-color) 100%)`,
                            },
                            9: {
                                top: `linear-gradient(to bottom, var(--main-color) 0%, var(--secondary-color) 40%, var(--secondary-color) 100%)`,
                                bottom: `linear-gradient(to bottom, var(--light-color) 0%, var(--light-color) 60%, var(--main-color) 100%)`,
                            },
                        }[index + 1] ?? fallbackGradient;

                    return (
                        <div key={index} style={getGradientColumnStyle(index, initialHeight) as React.CSSProperties}>
                            <div
                                className="gradient-column-top"
                                style={{
                                    width: '100%',
                                    borderRadius: 0,
                                    height: initialHeight,
                                    background: gradients.top,
                                }}
                            />
                            <div
                                style={{
                                    background: gradients.bottom,
                                    width: '100%',
                                    borderRadius: 0,
                                    height: `calc(100% - var(--initial-height))`,
                                }}
                            />
                        </div>
                    );
                })}
            </div>
            <style>
                {`
@keyframes growAnimation {
    0% {
        transform: scaleY(1);
    }
    50% {
        transform: scaleY(1.2);
    }
    100% {
        transform: scaleY(1);
    }
}

.image-model-gradient-animate-hover {
    transition: all 0.3s ease-in-out;
}

.gradient-column-top {
    transform-origin: top;
    will-change: transform;
    backface-visibility: hidden;
    perspective: 1000px;
}

.image-model-gradient-animate-hover .gradient-column-top {
    animation: growAnimation 6s ease-in-out infinite;
    animation-play-state: paused;
}

.image-model-gradient-animate-hover:hover .gradient-column-top {
    animation-play-state: running;
}

.image-model-gradient-animate-always .gradient-column-top {
    animation: growAnimation 6s ease-in-out infinite;
    animation-play-state: running;
}

.image-model-gradient-animate-hover:hover {
    cursor: pointer;
}`}
            </style>
        </div>
    );
}
